import React from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
  usePlayerProfile,
  usePlayerHighlights,
  useShareModal,
} from "../../hooks/player-public-profile";
import { usePlayerStats } from "../../components/search/hooks/usePlayerStats";
import { usePlayerInjuries } from "../../hooks/player-public-profile/usePlayerInjuries";
import {
  ProfileHeader,
  ProfileTabs,
  ProfileContent,
  HeaderActions,
  EditHighlightsButton,
  LoadingSpinner,
  ErrorMessage,
  NotFoundMessage,
} from "../../components/player-public-profile";
import PlayerHeroBanner from "../../components/common/PlayerHeroBanner";
import OpenToRepresentationBanner from "../../components/common/OpenToRepresentationBanner";
import HighlightsModal from "../../components/common/HighlightsModal";
import ShareActionSheet from "../../components/common/ShareActionSheet";
import StatisticsIcon from "../../assets/icons/StatisticsIcon";
import AttributesIcon from "../../assets/icons/AttributesIcon";
import HighlightsIcon from "../../assets/icons/HighlightsIcon";
import TransferHistoryIcon from "../../assets/icons/TransferHistoryIcon";
import Club from "../../assets/icons/Club";
import MarketValue from "../../assets/icons/MarketValue";
import { useMemo } from "react";

const PlayerProfile = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Use custom hooks for state management
  const {
    playerId,
    player,
    loading,
    error,
    currentRole,
    currentUserProfile,
    isOwnProfile,
    isAgentOwnThisPlayer,
    playerWyscoutId,
    activeTab,
    setActiveTab,
    tabs,
    representationRequest,
    handleRejectRequest,
    handleAcceptRequest,
    shareUrl,
    handleShare,
    attributes,
    setAttributes,
    uniqueTournaments,
    setUniqueTournaments,
    navigate,
  } = usePlayerProfile();

  const injuries = usePlayerInjuries(playerId); // Paket Injuries: hero pill + history block (Transfers tab)

  const {
    showHighlightsModal,
    closeHighlightsModal,
    openHighlightsModal,
    editableHighlights,
    editableHighlightsLoading,
    highlightsRefreshKey,
    handleHighlightsSave,
  } = usePlayerHighlights({
    isOwnProfile,
    isAgentOwnThisPlayer,
    playerWyscoutId,
  });

  const { showShareModal, openShareModal, closeShareModal } = useShareModal();

  // Prepare player data for stats hook
  const playerDataForStats = useMemo(
    () => ({
      ...player,
      identifiers: {
        wyscoutId: playerWyscoutId,
        sportmonksId:
          player?.sportmonks_player_id || player?.sportmonks_id || player?.id,
        sofascoreId: player?.sofascore_id || player?.sofascoreId || null,
      },
    }),
    [player, playerWyscoutId]
  );

  const {
    seasonStats,
    seasonOptions,
    selectedSeasonId,
    setSelectedSeasonId,
    statsLoading,
    selectedMatch,
    ratingChartData,
    ratingsLoading,
    ratingsError,
  } = usePlayerStats({
    playerData: playerDataForStats,
    playerId,
    setAttributes,
    wyscoutId: playerWyscoutId,
  });

  // Add icons to tabs
  const tabsWithIcons = useMemo(() => {
    return tabs.map((tab) => {
      let icon = null;
      switch (tab.id) {
        case "statistics":
          icon = StatisticsIcon;
          break;
        case "attributes":
          icon = AttributesIcon;
          break;
        case "value":
          icon = MarketValue;
          break;
        case "matches":
          icon = Club;
          break;
        case "highlights":
          icon = HighlightsIcon;
          break;
        case "transfers":
          icon = TransferHistoryIcon;
          break;
        default:
          break;
      }
      return { ...tab, Icon: icon };
    });
  }, [tabs]);

  // Loading state
  if (loading) {
    return <LoadingSpinner />;
  }

  // Error state
  if (error) {
    return <ErrorMessage message={error} />;
  }

  // Not found state
  if (!player) {
    return <NotFoundMessage />;
  }

  // Determine if edit highlights button should show
  const showEditButton =
    (isOwnProfile || isAgentOwnThisPlayer) && activeTab === "highlights";

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Back Button and Actions */}
      <ProfileHeader onBackClick={() => navigate(-1)}>
        <HeaderActions
          currentRole={currentRole}
          currentUserProfile={currentUserProfile}
          representationRequest={representationRequest}
          onRejectRequest={handleRejectRequest}
          onAcceptRequest={handleAcceptRequest}
          onShareClick={openShareModal}
          wsPlayerId={playerWyscoutId}
          playerName={player?.name}
        />
      </ProfileHeader>

      {/* Open to Representation Banner */}
      <div className="w-full px-4 md:px-10 pt-4">
        <OpenToRepresentationBanner player={player} />
      </div>

      {/* Hero Section Banner */}
      <div className="w-full px-4 md:px-10 pt-6">
        <PlayerHeroBanner player={player} showSimplePositionIfNoDetailed={true} availability={injuries?.availability || null} />
      </div>

      {/* Tabs */}
      <ProfileTabs
        tabs={tabsWithIcons}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isMobile={isMobile}
        showEditButton={showEditButton}
        editButton={
          <EditHighlightsButton
            onClick={openHighlightsModal}
            isLoading={editableHighlightsLoading}
            isMobile={isMobile}
          />
        }
        // Season props
        seasonOptions={seasonOptions}
        selectedSeasonId={selectedSeasonId}
        setSelectedSeasonId={setSelectedSeasonId}
      />

      {/* Content */}
      <ProfileContent
        player={player}
        playerId={playerId}
        activeTab={activeTab}
        injuries={injuries?.injuries || null}
        attributes={attributes}
        uniqueTournaments={uniqueTournaments}
        setAttributes={setAttributes}
        setUniqueTournaments={setUniqueTournaments}
        highlightsRefreshKey={highlightsRefreshKey}
        // Stats props
        seasonStats={seasonStats}
        seasonOptions={seasonOptions}
        selectedSeasonId={selectedSeasonId}
        setSelectedSeasonId={setSelectedSeasonId}
        statsLoading={statsLoading}
        ratingChartData={ratingChartData}
        selectedMatch={selectedMatch}
        ratingsLoading={ratingsLoading}
        ratingsError={ratingsError}
      />

      {/* Highlights Modal */}
      <HighlightsModal
        isOpen={showHighlightsModal}
        onClose={closeHighlightsModal}
        highlights={editableHighlights}
        editingAsAgent={isAgentOwnThisPlayer && !isOwnProfile}
        playerWyscoutId={playerWyscoutId}
        onSave={handleHighlightsSave}
      />

      {/* Share Modal */}
      <ShareActionSheet
        isOpen={showShareModal}
        onClose={closeShareModal}
        onShare={handleShare}
        title={`Share ${player?.name || "Player"} Profile`}
        shareUrl={shareUrl}
        shareType="player_public_profile"
        shareData={{
          player: {
            sportmonks_id:
              player?.sportmonks_player_id ||
              player?.sportmonks_id ||
              player?.id,
            name: player?.name || "Player Name",
          },
        }}
      />
    </div>
  );
};

export default PlayerProfile;
